/**
 * Jersey Mapping Utilities
 *
 * Utilities for auto-populating jersey images based on team abbreviations and player data.
 * Maps team abbreviations to blob-storage jersey URLs with support for team logos
 * and numbered jerseys. Supports NBA, NFL, and NCAA Men's Basketball sports.
 *
 * Every builder here yields no URL — '' where it returns a string, null where it
 * returns a nullable one — when no storage account is configured (CAE-676).
 */

import { SUPPORTED_SPORT_CODES, getFileExtForSport } from '@low6-pickem/sports-config';
import { getBlobAssetBaseUrl } from './blobStorage';

/** Session-level cache buster — set once on app load so replaced images are never stale */
const SESSION_CACHE_BUSTER = Date.now();

export type Sport = (typeof SUPPORTED_SPORT_CODES)[number] | 'Mixed';

/**
 * Manual/custom NCAAMB team assets in picks-core/ncaamb/teams/ that are not in the feed.
 * These appear in team selectors alongside feed teams when league is ncaamb.
 */
export const NCAAMB_MANUAL_ASSETS = [
  { externalId: 'Arbys-HOME', name: 'Arbys (Home)', abbreviation: 'Arbys-HOME' },
  { externalId: 'Arbys-AWAY', name: 'Arbys (Away)', abbreviation: 'Arbys-AWAY' },
] as const;

/**
 * Zero-pad a jersey number to 2 digits
 * @param jerseyNo - Jersey number
 * @returns Zero-padded string (e.g., "01", "15", "99")
 */
export function padJerseyNumber(jerseyNo: number | string): string {
  const num = typeof jerseyNo === 'string' ? jerseyNo : jerseyNo.toString();
  return num.padStart(2, '0');
}

/**
 * Generate team logo URL
 * @param teamAbbr - Team abbreviation (e.g., "DEN", "KC")
 * @param sport - Sport type (NBA, NFL, or NCAAMB)
 * @returns URL for team logo
 * @example
 * generateTeamLogoUrl('DEN', 'NBA') → '.../picks-core/nba/teams/DEN.png'
 * generateTeamLogoUrl('DUKE', 'NCAAMB') → '.../picks-core/ncaamb/teams/DUKE.png'
 */
export function generateTeamLogoUrl(teamAbbr: string, sport: Sport): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  const sportLower = sport.toLowerCase();
  // NCAA Football: non-IP helmet artwork (PNG) under /helmet (DKPLAT-559).
  if (sportLower === 'ncaafb')
    return `${base}/ncaafb/helmet/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  const fileExt = getFileExtForSport(sportLower);
  return `${base}/${sportLower}/teams/${teamAbbr}.${fileExt}?v=${SESSION_CACHE_BUSTER}`;
}

/**
 * Generate player jersey URL
 * @param teamAbbr - Team abbreviation (e.g., "DEN", "KC")
 * @param jerseyNo - Jersey number
 * @param sport - Sport type (NBA, NFL, or NCAAMB)
 * @returns URL for player's numbered jersey
 * @example
 * generatePlayerJerseyUrl('DEN', 59, 'NBA') → '.../picks-core/nba/jerseys/DEN/59.png'
 */
export function generatePlayerJerseyUrl(
  teamAbbr: string,
  jerseyNo: number | string,
  sport: Sport
): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  return `${base}/${sport.toLowerCase()}/jerseys/${teamAbbr}/${jerseyNo}.png?v=${SESSION_CACHE_BUSTER}`;
}

/**
 * Generate blank jersey URL for a team (alias for team logo)
 * @param teamAbbr - Team abbreviation (e.g., "DEN", "KC")
 * @param sport - Sport type (NBA or NFL)
 * @returns URL for team logo
 */
export function generateBlankJerseyUrl(teamAbbr: string, sport: Sport): string {
  return generateTeamLogoUrl(teamAbbr, sport);
}

/**
 * Generate team image URL from teams directory
 * Teams directory: NBA and NCAAMB use .png; most others use .svg
 * @param teamAbbr - Team abbreviation or asset name (e.g., "CHA", "DUKE", "Arbys-HOME", "Arbys-AWAY")
 * @param sport - Sport type (NBA, NFL, NCAAMB, or NHL)
 * @returns URL for team image from teams directory
 * @example
 * generateTeamImageUrl('CHA', 'NBA') → '.../picks-core/nba/teams/CHA.png'
 * generateTeamImageUrl('Arbys-HOME', 'NCAAMB') → '.../picks-core/ncaamb/teams/Arbys-HOME.png'
 */
export function generateTeamImageUrl(teamAbbr: string, sport: Sport): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  const sportLower = sport.toLowerCase();
  // NCAA Football: non-IP helmet artwork (PNG) under /helmet (DKPLAT-559).
  if (sportLower === 'ncaafb')
    return `${base}/ncaafb/helmet/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  const ext = getFileExtForSport(sportLower);
  return `${base}/${sportLower}/teams/${teamAbbr}.${ext}?v=${SESSION_CACHE_BUSTER}`;
}

/**
 * Generate team jersey image URL from team-jerseys directory
 * team-jerseys directory contains one .png file per team (e.g., ATL.png, BOS.png)
 * @param teamAbbr - Team abbreviation (e.g., "ATL", "BOS")
 * @param sport - Sport type (NBA, NFL, or NCAAMB)
 * @returns URL for team jersey image from team-jerseys directory
 * @example
 * generateTeamJerseyImageUrl('ATL', 'NBA') → '.../picks-core/nba/team-jerseys/ATL.png'
 */
export function generateTeamJerseyImageUrl(teamAbbr: string, sport: Sport): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  return `${base}/${sport.toLowerCase()}/team-jerseys/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
}

// Feed abbreviations that differ from blob storage folder names
const MLB_TEAM_ABBR_MAP: Record<string, string> = {
  AZ: 'ARI',
  ANA: 'LAA',
};

/**
 * Generate MLB team jersey URL
 * MLB jerseys are stored at mlb/jerseys/{ABBR}.png (just team abbreviation, no number)
 * @param teamAbbr - Team abbreviation (e.g., "NYY", "LAD")
 * @returns URL for team jersey image
 * @example
 * generateMLBTeamJerseyUrl('NYY') → '.../mlb/jerseys/NYY.png'
 */
export function generateMLBTeamJerseyUrl(teamAbbr: string): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  const folder = MLB_TEAM_ABBR_MAP[teamAbbr] || teamAbbr;
  return `${base}/mlb/jerseys/${folder}.png?v=${SESSION_CACHE_BUSTER}`;
}

/**
 * Generate team jersey/avatar URL for any sport
 * Each sport stores jerseys differently:
 *   NBA: nba/team-jerseys/{ABBR}.png
 *   MLB: mlb/jerseys/{ABBR}.png
 *   NFL: nfl/jerseys/{ABBR}.png
 * @param teamAbbr - Team abbreviation
 * @param sport - Sport code
 * @returns URL for team jersey image, or null if sport has no team jerseys
 */
export function getTeamAvatarUrl(teamAbbr: string, sport: Sport): string | null {
  const base = getBlobAssetBaseUrl();
  if (!base) return null;
  const s = sport.toLowerCase();
  if (s === 'mlb') return generateMLBTeamJerseyUrl(teamAbbr) || null;
  if (s === 'nba') return `${base}/nba/team-jerseys/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  if (s === 'nfl') return `${base}/nfl/jerseys/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  // NCAA Football: one non-IP TEAM jersey per team under /jerseys (DKPLAT-559).
  if (s === 'ncaafb') return `${base}/ncaafb/jerseys/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  return null;
}

/**
 * Generate player avatar URL for any sport
 * Uses numbered jerseys only where licensed (NBA), else falls back to team jersey
 *   NBA: nba/jerseys/{ABBR}/{NUM}.png (not zero-padded)
 *   NFL: nfl/jerseys/{ABBR}.png (team jersey only — DKPLAT-604)
 *   MLB: mlb/jerseys/{ABBR}.png (team jersey only, no numbered)
 * @param teamAbbr - Team abbreviation
 * @param sport - Sport code
 * @param jerseyNumber - Player jersey number (optional)
 * @returns URL for player/team jersey image, or null if not available
 */
export function getPlayerAvatarUrl(
  teamAbbr: string,
  sport: Sport,
  jerseyNumber?: string
): string | null {
  const base = getBlobAssetBaseUrl();
  if (!base) return null;
  const s = sport.toLowerCase();
  // NFL numbered player jerseys are no longer licensed for use (DKPLAT-604) —
  // NFL now falls through to the team jersey below, like MLB and NCAAFB.
  if (s === 'nba' && jerseyNumber) {
    // Use numbered jersey — not zero-padded
    const num = parseInt(jerseyNumber, 10);
    if (!isNaN(num)) {
      return `${base}/${s}/jerseys/${teamAbbr}/${num}.png?v=${SESSION_CACHE_BUSTER}`;
    }
  }
  // Fall back to team jersey, or team logo for NCAAMB players
  const teamJersey = getTeamAvatarUrl(teamAbbr, sport);
  if (teamJersey) return teamJersey;
  if (s === 'ncaamb') return `${base}/ncaamb/teams/${teamAbbr}.png?v=${SESSION_CACHE_BUSTER}`;
  return null;
}

/**
 * Generate MLB player headshot URL
 * Blob storage uses lowercase names with underscores, e.g. "sean_manaea.png", "a_j__puk.png", "mark_leiter_jr_.png"
 * @param teamAbbr - Team abbreviation (e.g., "NYY", "LAD")
 * @param firstName - Player first name
 * @param lastName - Player last name
 * @returns URL for player headshot
 * @example
 * generateMLBHeadshotUrl('NYM', 'Sean', 'Manaea') → '.../mlb/headshots/NYM/sean_manaea.png'
 * generateMLBHeadshotUrl('ARI', 'A.J.', 'Puk') → '.../mlb/headshots/ARI/a_j__puk.png'
 */
export function generateMLBHeadshotUrl(
  teamAbbr: string,
  firstName: string,
  lastName: string
): string {
  const base = getBlobAssetBaseUrl();
  if (!base) return '';
  const folder = MLB_TEAM_ABBR_MAP[teamAbbr] || teamAbbr;
  const normalize = (s: string) => s.toLowerCase().replace(/\./g, '_').replace(/\s+/g, '_');
  return `${base}/mlb/headshots/${folder}/${normalize(firstName)}_${normalize(lastName)}.png?v=${SESSION_CACHE_BUSTER}`;
}

// ---------------------------------------------------------------------------
// League IP (Intellectual Property) image resolvers
// ---------------------------------------------------------------------------
// A contest's `league_ip` flag decides which imagery the auto-fill bakes into
// the saved option/contest at admin build-time:
//
//   IP on  → licensed imagery: team LOGOS + player HEADSHOTS
//   IP off → generic imagery:  team AVATARS (jerseys) + player JERSEYS
//
// Coverage is uneven across sports, so each resolver applies a fail-safe: when
// the requested asset doesn't exist for the sport, it falls back to the
// nearest available asset rather than emitting a broken URL. The generators
// signal "unavailable" by returning `null`, so no network check is needed.
// (Mirrors Daily Reveal's render-time jersey↔headshot fallback.)

export interface ResolveTeamImageArgs {
  teamAbbr: string;
  sport: Sport;
  leagueIp: boolean;
}

/**
 * Resolve the team image URL for a contest's IP setting.
 *   IP on  → team logo (licensed; available for every sport).
 *   IP off → team avatar/jersey; falls back to the logo when the sport has no
 *            avatar (e.g. NCAAMB, soccer). The reverse gap (IP-off → logo) is
 *            a known least-bad default — see plan risks.
 */
export function resolveTeamImageUrl({ teamAbbr, sport, leagueIp }: ResolveTeamImageArgs): string {
  if (leagueIp) {
    return generateTeamLogoUrl(teamAbbr, sport);
  }
  return getTeamAvatarUrl(teamAbbr, sport) ?? generateTeamLogoUrl(teamAbbr, sport);
}

/**
 * The "third image" slot behind a player pick is a team mark, so it follows the
 * same IP rule as a team image (logo when on, avatar when off).
 */
export function resolveThirdImageUrl(args: ResolveTeamImageArgs): string {
  return resolveTeamImageUrl(args);
}

export interface ResolvePlayerImageArgs {
  teamAbbr: string;
  sport: Sport;
  jerseyNo?: number | string;
  firstName?: string;
  lastName?: string;
  leagueIp: boolean;
}

/**
 * Resolve the player image URL for a contest's IP setting.
 *   IP on  → player headshot. Only MLB has a headshot generator in admin, so
 *            other sports FAIL-SAFE to the jersey.
 *   IP off → player jersey/avatar (numbered jersey for NBA, team jersey for
 *            NFL/MLB, team logo for NCAAMB — via getPlayerAvatarUrl).
 * Returns null only when no image can be produced at all (no team abbr).
 */
export function resolvePlayerImageUrl({
  teamAbbr,
  sport,
  jerseyNo,
  firstName,
  lastName,
  leagueIp,
}: ResolvePlayerImageArgs): string | null {
  // No team abbreviation → no resolvable asset path. Bail rather than emit a
  // URL with an empty path segment (which would 404 / show a broken preview).
  if (!teamAbbr) return null;
  if (leagueIp && sport.toLowerCase() === 'mlb' && firstName && lastName) {
    return generateMLBHeadshotUrl(teamAbbr, firstName, lastName);
  }
  // IP off, or IP-on fail-safe (no headshot for this sport) → generic jersey.
  return getPlayerAvatarUrl(teamAbbr, sport, jerseyNo != null ? String(jerseyNo) : undefined);
}
